// SiteInvoicesTab.tsx
// Site page "Invoices" tab — every invoice assigned to this site, in one list:
//   • inbox emails assigned here (bv_invoice_email.site_id)
//   • manually-uploaded invoice PDFs (bv_bill.invoice_path with no email row)
// Row actions: [View] re-opens the split email/PDF popup; [Open Bill Run] jumps
// to the Bill Runs tab with that bill selected (existing shadow/compare flow).

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Loader2, Mail, FileText, ExternalLink, Inbox, Sparkles, Upload, Pencil, Trash2, AlertTriangle, CalendarDays, RefreshCw, Archive, ArchiveRestore } from 'lucide-react';
import {
  Card, CardContent,
} from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import { Checkbox } from '../../../foundation/altzero_base/base/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../foundation/altzero_base/base/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../foundation/altzero_base/base/components/ui/select';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import { billService } from '../services/billService';
import { RetailerPromptPicker } from './common/RetailerPromptPicker';
import { bvReportService } from '../services/bvReportService';
import { billEmailService } from '../services/billEmailService';
import { siteInvoiceService } from '../services/siteInvoiceService';
import { pickDroppedPdf, validateInvoicePdf } from '../utils/invoiceDrop';
import { validateBillPeriod } from '../utils/billPeriod';
import { isShadowOverridden } from '../utils/shadowOverride';
import { useBlockStrayFileDrop } from '../utils/useBlockStrayFileDrop';
import { useRowSelection } from '../utils/useRowSelection';
import { ArchiveInvoiceDialog } from './ArchiveInvoiceDialog';
import { archiveReasonLabel } from '../utils/archiveReasons';
import type { ArchiveReason } from '../utils/archiveReasons';
import {
  parseStateOf, parseErrorOf, shouldPollParses, activeParseElapsedMs, formatElapsed,
  PARSE_HANDOFF_GRACE_MS,
  type ParseState,
} from '../utils/parseState';
import { EmailPreviewDrawer } from './inbox/EmailPreviewDrawer';
import { InvoiceParseReview } from './bills/InvoiceParseReview';
import { InvoiceCompareDialog } from './bills/InvoiceCompareDialog';
import { isInvoiceEdited } from '../lib/effectiveInvoice';
import type { InboxEmail } from '../services/billEmailService';
import type { BvBill, ActualBillResult, BvSiteInvoice, ShadowBillSection } from '../types/billTypes';
import { billStatus } from '../utils/billStatus';
import { useBillVerdicts } from '../hooks/useBillVerdicts';
import type { BillVerdict } from '../lib/billVerdict';

const BUCKET = 'bv-attachments';

type Verdict = 'OVERCHARGE' | 'UNDERCHARGE' | 'MATCHED' | 'PENDING';

// The same verdict as every other screen (billVerdict, via billStatus).
function verdictOf(bill: BvBill | undefined, verdict: BillVerdict | null): Verdict {
  if (!bill || bill.shadow_result?.total == null || bill.actual_total == null) return 'PENDING';
  const s = billStatus(bill, verdict);
  return s === 'overcharge' ? 'OVERCHARGE' : s === 'undercharge' ? 'UNDERCHARGE' : 'MATCHED';
}

const VERDICT_STYLE: Record<Verdict, string> = {
  OVERCHARGE: 'bg-red-100 text-red-700',
  UNDERCHARGE: 'bg-amber-100 text-amber-700',
  MATCHED: 'bg-green-100 text-green-700',
  PENDING: 'bg-gray-100 text-gray-500',
};

// Keyed by ParseState, not by the raw `parsing_status` column. The two are not
// the same thing: a row that says 'parsing' in the database has either a live
// parse behind it or a dead one, and the whole complaint about this screen was
// that both drew as "Parsing…" forever.
const PARSE_LABEL: Record<ParseState, { label: string; cls: string } | null> = {
  parsed: { label: 'Parsed', cls: 'bg-green-50 text-green-700' },
  parsing: { label: 'Parsing…', cls: 'bg-blue-50 text-blue-700' },
  stalled: { label: 'Parse stalled', cls: 'bg-amber-50 text-amber-700' },
  pending: { label: 'Not parsed', cls: 'bg-amber-50 text-amber-700' },
  failed: { label: 'Parse failed', cls: 'bg-red-50 text-red-700' },
  none: null,
};

type EmailRow = Omit<InboxEmail, 'attachments'> & {
  attachments?: {
    id: string;
    parsing_status: string;
    parse_error: string | null;
    // Job 68/76: how long the row has sat in its current state is the only
    // evidence that a background parse died, so it has to be selected.
    updated_at: string | null;
    filename?: string;
    storage_path?: string | null;
  }[];
};

type InvoiceRow = {
  key: string;
  source: 'email' | 'manual';
  date: string;                  // received_at / bill created_at
  fromLabel: string;             // sender or filename
  /**
   * What distinguishes this invoice from another one on the same bill period —
   * the email subject, or the stored filename for a manual upload.
   *
   * `matchOrCreateBill` pairs an incoming invoice to a period by site and date
   * overlap alone, so two genuinely different invoices (different retailer
   * account numbers, different Gmail messages) can land on one `bv_bill`. Every
   * other column on this row is bill-level, so without this the two rows are
   * indistinguishable and read as an accidental duplicate.
   */
  detailLabel?: string | null;
  bill: BvBill | undefined;
  parseStatus: ParseState;
  /** Why the parse failed, when it did — from `parse_error`. */
  parseErrorText?: string | null;
  /** How long a live parse has been running, for the chip's elapsed count. */
  parseElapsedMs?: number | null;
  email?: EmailRow;              // for [View]
  needsPeriod?: boolean;         // assigned to the site but no bill period yet
};

/**
 * How many rows on screen point at each bill id.
 *
 * More than one means several invoices share a single bill period, which is a
 * real state this site's data gets into: `matchOrCreateBill` resolves an
 * incoming invoice to a period by site and date overlap alone, so two different
 * invoices for the same site and month resolve to the same `bv_bill`. The rows
 * then show identical period, total and verdict, because all of those are
 * bill-level, and archiving from either one retires the single shared bill.
 *
 * None of that is wrong given one bill — but it has to be said on screen, or
 * the list reads as a duplicate and the archive looks like it took two rows by
 * mistake.
 */
/**
 * The invoice whose figures are currently stored on a bill.
 *
 * A bill keeps ONE parsed result, one `actual_total` and one `invoice_path`
 * (invoiceParserService writes them keyed on the bill, not on the attachment).
 * When several invoices share the period, `invoice_path` is the only record of
 * which one the stored numbers actually came from — so it is what a warning has
 * to name before the user replaces them.
 */
function storedInvoiceName(bill: BvBill | undefined): string | null {
  if (!bill?.invoice_path) return null;
  return bill.invoice_path.split('/').pop() ?? null;
}

function billRowCounts(rows: InvoiceRow[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const r of rows) {
    if (!r.bill) continue;
    counts.set(r.bill.id, (counts.get(r.bill.id) ?? 0) + 1);
  }
  return counts;
}

// Job 81 — one row-shaping function shared by both sub-tabs, so an archived
// row renders with exactly the same columns as an active one.
// `includeUnboundEmails` keeps a bill-less email row (needsPeriod) out of the
// archived tab: there is no bill on it to archive, so it can only ever be
// active.
function buildInvoiceRows(
  bills: BvBill[],
  emails: EmailRow[],
  opts: { includeUnboundEmails: boolean },
): InvoiceRow[] {
  const billById = new Map(bills.map(b => [b.id, b]));

  const emailRows: InvoiceRow[] = emails
    .filter(e => (e.bill_id ? billById.has(e.bill_id) : opts.includeUnboundEmails))
    .map(e => {
      const bill = e.bill_id ? billById.get(e.bill_id) : undefined;
      return {
        key: `email-${e.id}`,
        source: 'email' as const,
        date: e.received_at,
        fromLabel: e.sender_name || e.sender_email,
        detailLabel: e.subject ?? null,
        bill,
        // One derivation for the whole feature (utils/parseState). The local
        // version this replaced had no 'stalled': a parse killed by a server
        // restart is written as 'parsing' and nothing ever corrects it, so the
        // row said "Parsing…" for as long as the record existed.
        parseStatus: parseStateOf(e),
        parseErrorText: parseErrorOf(e),
        parseElapsedMs: activeParseElapsedMs(e),
        email: e,
        // An email assigned to this site but with no resolved bill period (e.g.
        // auto-matched by NMI) still belongs here — flag it rather than hide it.
        needsPeriod: !bill,
      };
    });

  // Manual invoices: bills with an uploaded PDF that no inbox email points to,
  // deduped both by bill id and by storage path — an assigned email and a
  // manual bill can point at the same stored PDF, and that must be one row.
  // Dedup runs against every email for the site (not just this scope's rows),
  // so an archived bill whose PDF an email also references never draws twice.
  const emailBillIds = new Set(emails.map(e => e.bill_id).filter(Boolean));
  const emailPaths = new Set(
    emails.flatMap(e => (e.attachments ?? []).map(a => a.storage_path).filter(Boolean)),
  );
  const manualRows: InvoiceRow[] = bills
    .filter(b => b.invoice_path && !emailBillIds.has(b.id) && !emailPaths.has(b.invoice_path))
    .map(b => ({
      key: `manual-${b.id}`,
      source: 'manual' as const,
      date: b.created_at,
      fromLabel: b.invoice_path!.split('/').pop() ?? 'invoice.pdf',
      detailLabel: null,
      bill: b,
      parseStatus: b.actual_parsed_result ? 'parsed' : 'pending',
    }));

  return [...emailRows, ...manualRows].sort((a, b) => (a.date < b.date ? 1 : -1));
}

type Props = {
  siteId: string;
  orgId: string;
  onOpenBillRun: (billId: string) => void;
};

export function SiteInvoicesTab({ siteId, orgId, onOpenBillRun }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewEmail, setPreviewEmail] = useState<InboxEmail | null>(null);
  const [parsingKey, setParsingKey] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  // When the last background parse was asked for, plus its grace window — see
  // the poll below. 0 = none outstanding.
  const [parseHandoffUntil, setParseHandoffUntil] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Parsed-result editor (same editor as Bill Runs)
  const [editingBill, setEditingBill] = useState<BvBill | null>(null);
  /**
   * Set when "Edit parsed" is pressed on a bill period that several invoices
   * share. Corrections save onto the bill, so they rewrite the figures for all
   * of them — the user gets told before the editor opens, not after they save.
   */
  const [editWarnBill, setEditWarnBill] = useState<BvBill | null>(null);

  /**
   * Removing one of several invoices that resolved to the same bill period.
   *
   * The period can only hold one invoice's figures, so two invoices on it means
   * one of them is not represented and the totals on screen belong to whichever
   * parsed last. Deleting the surplus email leaves the period with the single
   * invoice everything downstream already assumes.
   */
  const [removingRow, setRemovingRow] = useState<InvoiceRow | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  /**
   * Set after removing the invoice whose figures the bill was holding. Deleting
   * the email does not undo the parse, so the period keeps numbers read from a
   * document that is no longer there until the remaining invoice is re-parsed.
   */
  const [staleFiguresNotice, setStaleFiguresNotice] = useState<string | null>(null);
  const [comparingBill, setComparingBill] = useState<BvBill | null>(null);
  const [editNotice, setEditNotice] = useState<string | null>(null);

  // Delete confirmation for an uploaded invoice PDF
  const [deleting, setDeleting] = useState<InvoiceRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Bill period editor (job 48) — correcting the period used to mean deleting the
  // invoice and uploading it again.
  const [editingPeriod, setEditingPeriod] = useState<InvoiceRow | null>(null);
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [periodBusy, setPeriodBusy] = useState(false);
  const [periodError, setPeriodError] = useState<string | null>(null);

  // Manual upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [bills, setBills] = useState<BvBill[]>([]);
  const verdictFor = useBillVerdicts(bills);
  const [uploadBillId, setUploadBillId] = useState('');
  // 'auto' is the default: the parser reads the billing period off the PDF and
  // overwrites bill_from/bill_to anyway (invoiceParserService — "the invoice is
  // the source of truth for the billing period"), so making the user type dates
  // first only invited them to type dates that were then discarded.
  const [periodMode, setPeriodMode] = useState<'auto' | 'existing' | 'new'>('auto');
  const [newFrom, setNewFrom] = useState('');
  const [newTo, setNewTo] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Re-parse dialog. Opened instead of parsing immediately when a result already
  // exists, because a re-parse both discards "Edit parsed" corrections AND can
  // legitimately be narrowed to the one section that came out wrong.
  const [reparseRow, setReparseRow] = useState<InvoiceRow | null>(null);
  const [reparseSections, setReparseSections] = useState<ShadowBillSection[]>([]);
  const [reparseNotes, setReparseNotes] = useState('');
  // undefined = leave the bill's stored retailer alone; a value (or null) = a
  // deliberate choice this parse should record and use.
  const [reparseRetailerId, setReparseRetailerId] = useState<string | null | undefined>(undefined);
  // Whether this bill's report has already gone to the client. Re-parsing after
  // that changes figures the client already holds a PDF of.
  const [sentReportAt, setSentReportAt] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Reference invoices (job 55) — historic invoices attached to the site with no
  // bill period. Never parsed, never in any total.
  const [refInvoices, setRefInvoices] = useState<BvSiteInvoice[]>([]);
  const [refUploadOpen, setRefUploadOpen] = useState(false);
  const [refNote, setRefNote] = useState('');
  const [refUploading, setRefUploading] = useState(false);
  const [refError, setRefError] = useState<string | null>(null);
  const [refDragOver, setRefDragOver] = useState(false);
  const refFileRef = useRef<HTMLInputElement>(null);
  const [refDeleting, setRefDeleting] = useState<BvSiteInvoice | null>(null);
  const [refDeleteBusy, setRefDeleteBusy] = useState(false);

  // Job 81 — Active/Archived split. Local to this tab: the site page's own tab
  // union is not involved. Both lists are loaded every time (see load()) so
  // both sub-tab badges always show a real count, never an em-dash.
  const [scope, setScope] = useState<'active' | 'archived'>('active');
  const [activeRows, setActiveRows] = useState<InvoiceRow[]>([]);
  const [archivedRows, setArchivedRows] = useState<InvoiceRow[]>([]);
  const [archiving, setArchiving] = useState<InvoiceRow[] | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  // A denied/partial restore is not a page-load failure either — same
  // reasoning as archive above, but there is no dialog to show it in, so it
  // gets its own dismissible inline banner above the table (mirrors
  // BillsListPage's `actionError`), never the page-level `error` that
  // replaces the whole tab.
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const selection = useRowSelection();
  // What the table actually renders — an in-memory swap between the two
  // already-loaded lists, not a reload, so switching sub-tabs costs nothing.
  const rows = scope === 'active' ? activeRows : archivedRows;
  const sharedBillCounts = useMemo(() => billRowCounts(rows), [rows]);

  /**
   * Archiving acts on the bill period, not on the individual invoice. When the
   * rows being archived sit on a period that other invoices also resolved to,
   * those other invoices leave the Active list as well — say so before the user
   * confirms rather than letting rows vanish unexplained.
   */
  const sharedPeriodNote = useMemo(() => {
    if (!archiving?.length) return null;
    const billIds = new Set(archiving.map(r => r.bill?.id).filter(Boolean) as string[]);
    const alsoAffected = [...billIds].reduce((sum, id) => {
      const onPeriod = sharedBillCounts.get(id) ?? 1;
      const selectedHere = archiving.filter(r => r.bill?.id === id).length;
      return sum + Math.max(0, onPeriod - selectedHere);
    }, 0);
    if (alsoAffected === 0) return null;
    return (
      `${alsoAffected} other invoice${alsoAffected === 1 ? '' : 's'} resolved to the same bill ` +
      `period${billIds.size === 1 ? '' : 's'} and will be archived too. Archiving retires the period, ` +
      `not a single document. To take off just one copy of a re-sent invoice, cancel and use ` +
      `"Remove" on that row instead.`
    );
  }, [archiving, sharedBillCounts]);

  // A selection surviving a sub-tab switch would let a user archive (or
  // restore) rows they can no longer see.
  useEffect(() => {
    selection.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Stop a near-miss drop from navigating away while a dialog is open.
  useBlockStrayFileDrop(uploadOpen || refUploadOpen);

  // `silent` is for the parse poll: it refreshes the rows without dropping the
  // table back to its loading state, so a background refresh cannot make the
  // screen flicker or steal focus from the dialog on top of it.
  const load = useCallback(async (opts?: { silent?: boolean }) => {
    // A background poll must not disturb what the user has ticked — the poll
    // fires every 4s for as long as a parse is running, which is exactly
    // when someone is likely to be ticking rows to archive. Only a visible
    // reload (mount, or an action that rewrote which rows are on screen,
    // like archive/restore) invalidates a selection; a silent refresh just
    // re-reads status on the same rows and leaves it alone.
    if (!opts?.silent) selection.clear();
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      // Both scopes, every time — not just the one the user is looking at.
      // That's one extra query, but it keeps both sub-tab badges honest (a
      // real count on each, never an em-dash for the one not in view) and
      // means switching sub-tabs is a free in-memory swap rather than a
      // second round trip.
      const [activeBillsRes, archivedBillsRes, emailsRes, refRes] = await Promise.all([
        billService.listBySite(siteId, 'active'),
        billService.listBySite(siteId, 'archived'),
        supabase
          .from('bv_invoice_email')
          .select('*, attachments:bv_invoice_email_attachment(id, parsing_status, parse_error, updated_at, filename, storage_path)')
          .eq('site_id', siteId)
          .order('received_at', { ascending: false }),
        siteInvoiceService.list(siteId),
      ]);
      if (!activeBillsRes.success) throw new Error(activeBillsRes.error ?? 'Failed to load bills');
      if (!archivedBillsRes.success) throw new Error(archivedBillsRes.error ?? 'Failed to load archived bills');
      if (emailsRes.error) throw new Error(emailsRes.error.message);
      setRefInvoices(refRes.success ? (refRes.data ?? []) : []);

      const activeBills = activeBillsRes.data ?? [];
      const archivedBills = archivedBillsRes.data ?? [];
      // The manual-upload "existing period" picker only makes sense against a
      // live bill period — an archived one is not somewhere a new invoice
      // should land.
      setBills(activeBills);
      const emails = (emailsRes.data ?? []) as EmailRow[];

      setActiveRows(buildInvoiceRows(activeBills, emails, { includeUnboundEmails: true }));
      setArchivedRows(buildInvoiceRows(archivedBills, emails, { includeUnboundEmails: false }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  // Keep the latest load in a ref so the poll interval can call it without being
  // torn down and recreated every time `rows` changes.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  // The bug this fixes: /inbox/:id/retry-parse is fire-and-forget — it returns
  // as soon as the parse is HANDED OFF, not when it finishes. This tab loaded
  // once and never again, so the row it drew from that moment ('downloading')
  // was the row it kept drawing. The parse could finish, fail, or die with the
  // server and the screen said "Parsing…" either way.
  //
  // A single interval, armed while something is genuinely parsing OR a parse was
  // just asked for, and torn down the moment neither holds (including when a
  // parse goes stalled — a dead parse will not report anything, so polling for
  // it is a request every 4s forever; Refresh is the way back from that).
  // Silent, so rows never flicker back to a skeleton and the open editor dialog
  // is untouched.
  //
  // The handoff window is what stops the poll from missing its own parse: the
  // retry endpoint answers before the attachment has been claimed, so the
  // refresh that follows it still reads 'pending' or 'failed' and "is anything
  // parsing?" was answered no — after which nothing ever asked again.
  // Archived rows never need parse polling — poll on the active list
  // regardless of which sub-tab is currently on screen, so opening Archived
  // does not silently stop the poll for a parse still running on Active.
  const shouldPoll = shouldPollParses(
    activeRows.map(r => r.email).filter((e): e is EmailRow => !!e),
    parseHandoffUntil,
  );
  useEffect(() => {
    if (!shouldPoll) return;
    const id = setInterval(() => { void loadRef.current({ silent: true }); }, 4000);
    return () => clearInterval(id);
  }, [shouldPoll]);

  // Silent, so the table does not drop to a skeleton and any open dialog stays
  // put — the spinner on the button is the whole progress indication.
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await load({ silent: true });
    } finally {
      setRefreshing(false);
    }
  };

  // Same parsing pipeline as everywhere else: email invoices go through the
  // inbox retry-parse endpoint; manual PDFs through the bill parse endpoint.
  // On success the review screen opens straight away — parsed values are never
  // silently accepted without eyes on the PDF.
  const handleParse = async (
    row: InvoiceRow,
    options?: { sections?: ShadowBillSection[]; extraInstructions?: string; retailerId?: string | null },
  ) => {
    setParsingKey(row.key);
    setParseError(null);
    // The two endpoints do NOT mean the same thing by "success", and treating
    // them alike is what made this screen lie:
    //
    //   POST /bills/:id/parse-invoice   awaits parseInvoicePdf — success means
    //                                   the invoice is parsed, right now.
    //   POST /inbox/:id/retry-parse     fires parseAttachmentIntoBill and
    //                                   returns immediately — success means
    //                                   only that the work was handed off.
    //
    // So the email path must not announce a result or open the review editor: it
    // refreshes, leaves the row reading "Parsing…", and the poll above reports
    // what actually happened.
    const isBackground = row.source === 'email' && !!row.email;
    try {
      if (isBackground && row.email) {
        const res = await billEmailService.retryParse(row.email.id, options?.retailerId, {
          sections: options?.sections,
          extraInstructions: options?.extraInstructions,
        });
        if (!res.success) throw new Error(res.error ?? 'Could not start the parse');
        // Arm the poll on the ASK, not on what the next refresh happens to see.
        // The claim that writes 'downloading' lands after this call returns, so
        // the refresh below can still read the pre-parse status.
        setParseHandoffUntil(Date.now() + PARSE_HANDOFF_GRACE_MS);
      } else if (row.bill?.invoice_path) {
        const res = await billService.parseInvoice(row.bill.id, row.bill.invoice_path, options);
        if (!res.success) throw new Error(res.error ?? 'Parse failed');
      }
      await load({ silent: true });
      // Only the awaited path has a finished result to review.
      if (!isBackground && row.bill) {
        const fresh = await billService.getById(row.bill.id);
        if (fresh.success && fresh.data?.actual_parsed_result) setEditingBill(fresh.data);
      }
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Parse failed');
    } finally {
      setParsingKey(null);
    }
  };

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  const fmtPeriod = (b?: BvBill) =>
    b ? `${fmtDate(b.bill_from)} – ${fmtDate(b.bill_to)}` : '—';

  // Manual upload: pick/create the bill period, upload the PDF, auto-parse.
  // Same storage path + parse pipeline as the Bill Runs invoice panel.
  const handleUpload = async (file: File | null) => {
    const invalid = validateInvoicePdf(file);
    if (invalid || !file) { setUploadError(invalid); return; }

    setUploading(true);
    setUploadError(null);
    try {
      let billId = uploadBillId;
      // Set when WE created the bill in this upload. bill_from/bill_to are NOT
      // NULL, so an 'auto' upload has to seed placeholder dates and let the
      // parser correct them. If the parse then fails there is nothing to
      // correct them with, and a bill carrying invented dates is worse than no
      // bill — it silently pollutes bv_bill_run_queue. So we roll it back.
      let createdBillId: string | null = null;
      if (periodMode !== 'existing') {
        if (periodMode === 'new' && (!newFrom || !newTo)) {
          throw new Error('Pick the bill period dates first');
        }
        // Placeholder for 'auto' — a zero-length period today, replaced by the
        // invoice's own dates during parse. Deliberately not a guessed month:
        // if it survives, it should look obviously wrong rather than plausible.
        const today = new Date().toISOString().slice(0, 10);
        const created = await billService.create({
          site_id: siteId,
          bill_from: periodMode === 'new' ? newFrom : today,
          bill_to: periodMode === 'new' ? newTo : today,
          peak_kwh: '', shoulder_kwh: '', off_peak_kwh: '', feed_in_kwh: '', peak_demand_kva: '', notes: '',
        });
        if (!created.success || !created.data) throw new Error(created.error ?? 'Failed to create bill period');
        billId = created.data.id;
        createdBillId = created.data.id;
      }
      if (!billId) throw new Error('Select a bill period first');

      const uid = crypto.randomUUID();
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `${orgId}/bills/${billId}/${uid}_${safe}`;

      const { error: uploadErr } = await supabase.storage
        .from(BUCKET)
        .upload(storagePath, file, { upsert: false });
      if (uploadErr) throw new Error(uploadErr.message);

      const saved = await billService.saveInvoicePath(billId, storagePath);
      if (!saved.success) {
        await supabase.storage.from(BUCKET).remove([storagePath]);
        throw new Error(saved.error ?? 'Failed to link invoice to bill');
      }

      // Auto-parse straight away — same pipeline as everywhere else. The result
      // is checked rather than discarded: on an auto-created bill the parse is
      // the ONLY thing that can supply a real billing period.
      const parsed = await billService.parseInvoice(billId, storagePath);
      // Roll back ONLY a bill we invented dates for. In 'new' mode the dates
      // came from the user and stand on their own, so a failed parse leaves the
      // bill alone and just reports — deleting it would discard their input.
      const rollbackId = periodMode === 'auto' ? createdBillId : null;
      if (!parsed.success) {
        if (rollbackId) {
          // Removes the storage object too (billService.delete reads
          // invoice_path first), so a failed upload leaves nothing behind.
          await billService.delete(rollbackId);
          throw new Error(
            `${parsed.error ?? 'Parse failed'} — the invoice could not be read, so no bill period was created.`
            + ' Upload again, or choose "Enter dates" and set the period by hand.',
          );
        }
        throw new Error(parsed.error ?? 'Parse failed');
      }
      if (rollbackId && !(parsed.data?.extracted_bill_from && parsed.data?.extracted_bill_to)) {
        // Parsed, but with no period — the placeholder dates would survive and
        // read as a real bill. Same rollback.
        await billService.delete(rollbackId);
        throw new Error(
          'The invoice parsed but no billing period could be read from it.'
          + ' Choose "Enter dates" and set the period by hand.',
        );
      }

      setUploadOpen(false);
      setUploadBillId('');
      setPeriodMode('auto');
      setNewFrom('');
      setNewTo('');
      await load();

      // Straight into the PDF-vs-parsed review.
      const fresh = await billService.getById(billId);
      if (fresh.success && fresh.data?.actual_parsed_result) setEditingBill(fresh.data);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // A bill period has to exist before the PDF has anywhere to go, so a drop
  // without one reports that instead of silently doing nothing.
  const periodReady = periodMode === 'auto' ? true
    : periodMode === 'new' ? !!(newFrom && newTo)
    : !!uploadBillId;

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    if (!periodReady) {
      setUploadError(periodMode === 'new' ? 'Pick the bill period dates first' : 'Select a bill period first');
      return;
    }
    const file = pickDroppedPdf(e.dataTransfer);
    void handleUpload(file as File);
  };

  // Only manually-uploaded PDFs are deletable here — an email-sourced invoice is
  // owned by the inbox row, so removing it belongs to the inbox flow.
  const handleDelete = async () => {
    if (!deleting?.bill) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const res = await billService.removeInvoice(deleting.bill.id);
      if (!res.success) throw new Error(res.error ?? 'Delete failed');
      setDeleting(null);
      await load();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleteBusy(false);
    }
  };

  // Reference invoice upload (job 55) — no bill period involved.
  const handleRefUpload = async (file: File | null) => {
    const invalid = validateInvoicePdf(file);
    if (invalid || !file) { setRefError(invalid); return; }
    setRefUploading(true);
    setRefError(null);
    try {
      const res = await siteInvoiceService.upload(siteId, file, refNote);
      if (!res.success) throw new Error(res.error ?? 'Upload failed');
      setRefUploadOpen(false);
      setRefNote('');
      await load();
    } catch (err) {
      setRefError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setRefUploading(false);
    }
  };

  const handleRefDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setRefDragOver(false);
    if (refUploading) return;
    void handleRefUpload(pickDroppedPdf(e.dataTransfer) as File);
  };

  const handleRefDelete = async () => {
    if (!refDeleting) return;
    setRefDeleteBusy(true);
    try {
      const res = await siteInvoiceService.remove(refDeleting.id);
      if (!res.success) throw new Error(res.error ?? 'Delete failed');
      setRefDeleting(null);
      await load();
    } catch (err) {
      setRefError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setRefDeleteBusy(false);
    }
  };

  const handleRefView = async (inv: BvSiteInvoice) => {
    const url = await siteInvoiceService.getSignedUrl(inv.storage_path);
    if (url) window.open(url, '_blank', 'noopener');
  };

  const openPeriodEditor = (row: InvoiceRow) => {
    if (!row.bill) return;
    setPeriodError(null);
    setPeriodFrom(row.bill.bill_from);
    setPeriodTo(row.bill.bill_to);
    setEditingPeriod(row);
  };

  const handleSavePeriod = async () => {
    if (!editingPeriod?.bill) return;
    setPeriodBusy(true);
    setPeriodError(null);
    try {
      const res = await billService.updatePeriod(editingPeriod.bill.id, periodFrom, periodTo);
      if (!res.success) throw new Error(res.error ?? 'Could not update the period');
      setEditingPeriod(null);
      await load();
    } catch (err) {
      setPeriodError(err instanceof Error ? err.message : 'Could not update the period');
    } finally {
      setPeriodBusy(false);
    }
  };

  const handleSaveEdited = async (result: ActualBillResult, note: string) => {
    if (!editingBill) return;
    const res = await billService.applyEditedInvoice(editingBill.id, { result, note });
    if (!res.success || !res.data) throw new Error(res.error ?? 'Save failed');

    // Correcting a kWh volume moves the usage the EXPECTED invoice is priced
    // against, and re-prices it. That is the right behaviour, but it must not be
    // silent — the variance a client is shown just changed.
    const { usageUpdated, repriced } = res.data;
    setEditNotice(
      usageUpdated.length === 0
        ? 'Corrections saved.'
        : `Corrections saved. Usage updated (${usageUpdated.join(', ')})`
          + `${repriced ? ' and the expected invoice re-priced.' : '.'}`,
    );
    setEditingBill(null);
    await load();
  };

  // Job 81 — archive with a reason. A partial result still moved some rows,
  // so reload either way: showing a stale list beside "2 of 3 failed" is
  // worse than the error alone, and the successes need to be visible.
  const handleArchive = async (reason: ArchiveReason, note: string) => {
    const ids = (archiving ?? []).map(r => r.bill?.id).filter(Boolean) as string[];
    if (!ids.length) return;
    setArchiveBusy(true);
    setArchiveError(null);
    try {
      const res = await billService.archive(ids, reason, note);
      await load();
      if (!res.success) { setArchiveError(res.error ?? 'Archive failed'); return; }
      setArchiving(null);
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Archive failed');
    } finally {
      setArchiveBusy(false);
    }
  };

  // Job 81 — restore. Mirrors handleArchive's ordering: reload FIRST, then set
  // the error, so the reload (which clears the row selection) cannot wipe a
  // failure message that was set before it ran. On failure the selection is
  // put back so the user can retry without re-picking rows, and the failure
  // renders as a non-fatal inline banner (restoreError) rather than the
  // page-level `error`, which would replace the whole tab with a bare line.
  /** True when the bill's stored figures were read from THIS row's attachment. */
  const ownsStoredFigures = (r: InvoiceRow): boolean => {
    const path = r.bill?.invoice_path;
    if (!path) return false;
    const storedFile = path.split('/').pop();
    // Match on the stored path first; fall back to the filename, because an
    // attachment re-uploaded or moved keeps its name but not its full path, and
    // getting this wrong means the warning silently never appears.
    return (r.email?.attachments ?? []).some(a =>
      (a.storage_path && path.includes(a.storage_path))
      || (!!storedFile && !!a.filename && a.filename === storedFile),
    );
  };

  const handleRemoveFromPeriod = async () => {
    const row = removingRow;
    if (!row?.email) return;
    setRemoveBusy(true);
    setRemoveError(null);
    const wasHoldingFigures = ownsStoredFigures(row);
    const keptLabel = rows.find(o => o.bill?.id === row.bill?.id && o.key !== row.key)?.detailLabel ?? null;
    try {
      // Kept and ignored rather than deleted: a deleted email is re-ingested by
      // the next sync and lands back on this bill.
      const res = await billEmailService.removeFromBill(row.email.id);
      if (!res.success) throw new Error(res.error ?? 'Remove failed');
      setRemovingRow(null);
      await load();
      // Removing the email does not roll back what it wrote to the bill.
      setStaleFiguresNotice(
        wasHoldingFigures
          ? `The figures on this bill period were read from the invoice you just removed. ` +
            `Re-parse ${keptLabel ? `"${keptLabel}"` : 'the remaining invoice'} so the period ` +
            `matches the document that is still on it.`
          : null,
      );
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setRemoveBusy(false);
    }
  };

  const handleRestore = async (targets: InvoiceRow[]) => {
    const ids = targets.map(r => r.bill?.id).filter(Boolean) as string[];
    if (!ids.length) return;
    setRestoreError(null);
    const res = await billService.restore(ids);
    await load();
    if (!res.success) {
      setRestoreError(res.error ?? 'Restore failed');
      ids.forEach(id => selection.toggle(id));
      return;
    }
  };

  if (loading) {
    return <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;
  }
  if (error) {
    return <p className="py-8 text-center text-sm text-red-600">{error}</p>;
  }

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          All invoices assigned to this site — from the inbox or uploaded manually.
        </p>
        <div className="flex items-center gap-2">
          {/* The poll stops on purpose once a parse goes stalled — a dead parse
              reports nothing, so following it is a request every 4s for as long
              as the tab is open. That left no way to pick up a parse that did
              finish afterwards except reloading the whole page. */}
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Re-read the parse status of every invoice on this site"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
            Refresh
          </Button>
          <Button size="sm" className="gap-1.5" onClick={() => { setUploadError(null); setUploadOpen(true); }}>
            <Upload className="h-3.5 w-3.5" /> Upload Invoice
          </Button>
        </div>
      </div>

      {editNotice && (
        <div className="mb-3 rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-800 flex items-start justify-between gap-3">
          <span>{editNotice}</span>
          <button onClick={() => setEditNotice(null)} className="text-blue-500 hover:text-blue-700 shrink-0">&times;</button>
        </div>
      )}

      {parseError && (
        <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{parseError}</p>
      )}

      {/* Job 81 — Active/Archived sub-tabs. Both counts are real: both lists
          are loaded every time (see load()), so neither badge ever falls back
          to an em-dash for the tab not currently in view. */}
      <div className="mb-3 inline-flex rounded-lg border bg-gray-50 p-0.5">
        <button
          type="button"
          data-testid="site-invoices-scope-active"
          onClick={() => setScope('active')}
          className={cn(
            'rounded-md px-3 py-1.5 text-xs font-medium transition',
            scope === 'active' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
          )}
        >
          Active ({activeRows.length})
        </button>
        <button
          type="button"
          data-testid="site-invoices-scope-archived"
          onClick={() => setScope('archived')}
          className={cn(
            'rounded-md px-3 py-1.5 text-xs font-medium transition',
            scope === 'archived' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
          )}
        >
          Archived ({archivedRows.length})
        </button>
      </div>

      {selection.count > 0 && (
        <div className="mb-2 flex items-center gap-2 rounded-md border bg-blue-50/50 px-3 py-2">
          <span className="text-xs text-gray-600">{selection.count} selected</span>
          {scope === 'active' ? (
            <Button
              size="sm" variant="outline" className="gap-1.5"
              data-testid="invoice-archive-selected"
              onClick={() => {
                setArchiveError(null);
                setArchiving(rows.filter(r => r.bill && selection.isSelected(r.bill.id)));
              }}
            >
              <Archive className="h-3.5 w-3.5" /> Archive selected
            </Button>
          ) : (
            <Button
              size="sm" variant="outline" className="gap-1.5"
              onClick={() => void handleRestore(rows.filter(r => r.bill && selection.isSelected(r.bill.id)))}
            >
              <ArchiveRestore className="h-3.5 w-3.5" /> Restore selected
            </Button>
          )}
        </div>
      )}

      {/* Job 81 — a denied/partial restore renders here, above the table, so
          the rows that stayed archived (and every other invoice) remain on
          screen rather than being replaced by the page-level error state. */}
      {restoreError && (
        <div
          data-testid="site-invoices-restore-error"
          className="mb-3 flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          <span>{restoreError}</span>
          <button
            type="button"
            onClick={() => setRestoreError(null)}
            className="shrink-0 text-red-400 hover:text-red-600"
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}

      {/* Survives the remove dialog closing: deleting the email does not undo
          what its parse wrote to the bill, and nothing else would say so. */}
      {staleFiguresNotice && (
        <div
          data-testid="site-invoices-stale-figures"
          className="mb-3 flex items-start justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          <span className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
            {staleFiguresNotice}
          </span>
          <button
            type="button"
            onClick={() => setStaleFiguresNotice(null)}
            className="shrink-0 text-amber-500 hover:text-amber-700"
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}

      {rows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-12 gap-2 text-gray-400">
            <Inbox className="h-8 w-8" />
            <p className="text-sm">No invoices assigned to this site yet</p>
            <p className="text-xs">Assign one from the Inbox, or click Upload Invoice above.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="w-10 py-2.5 px-4">
                      <Checkbox
                        checked={selection.allSelected(rows.map(r => r.bill?.id).filter(Boolean) as string[])}
                        onCheckedChange={() => selection.toggleAll(rows.map(r => r.bill?.id).filter(Boolean) as string[])}
                        aria-label="Select all invoices"
                      />
                    </th>
                    <th className="py-2.5 px-4 text-left text-xs font-medium text-gray-500">Source</th>
                    <th className="py-2.5 px-4 text-left text-xs font-medium text-gray-500">Received</th>
                    <th className="py-2.5 px-4 text-left text-xs font-medium text-gray-500">From / File</th>
                    <th className="py-2.5 px-4 text-left text-xs font-medium text-gray-500">Bill Period</th>
                    <th className="py-2.5 px-4 text-left text-xs font-medium text-gray-500">Parse</th>
                    <th className="py-2.5 px-4 text-right text-xs font-medium text-gray-500">Actual Total</th>
                    <th className="py-2.5 px-4 text-left text-xs font-medium text-gray-500">Verdict</th>
                    <th className="py-2.5 px-4 text-right text-xs font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(r => {
                    const verdict = verdictOf(r.bill, r.bill ? verdictFor(r.bill) : null);
                    const parse = PARSE_LABEL[r.parseStatus] ?? PARSE_LABEL.pending;
                    return (
                      <tr key={r.key} className="hover:bg-gray-50" data-testid="site-invoices-row">
                        <td className="py-2.5 px-4">
                          {r.bill && (
                            <Checkbox
                              data-testid="invoice-select"
                              checked={selection.isSelected(r.bill.id)}
                              onCheckedChange={() => selection.toggle(r.bill!.id)}
                              aria-label={`Select invoice ${r.fromLabel}`}
                            />
                          )}
                        </td>
                        <td className="py-2.5 px-4">
                          <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                            {r.source === 'email' ? <Mail className="h-3.5 w-3.5 text-blue-500" /> : <FileText className="h-3.5 w-3.5 text-gray-400" />}
                            {r.source === 'email' ? 'Email' : 'Manual'}
                          </span>
                        </td>
                        <td className="py-2.5 px-4 text-gray-700 whitespace-nowrap">{fmtDate(r.date)}</td>
                        <td className="py-2.5 px-4 text-gray-700 max-w-[260px]" title={r.detailLabel ? `${r.fromLabel} — ${r.detailLabel}` : r.fromLabel}>
                          <span className="block truncate">{r.fromLabel}</span>
                          {/* Without this, two invoices sharing a bill period are
                              indistinguishable — every other column is bill-level. */}
                          {r.detailLabel && (
                            <span className="block truncate text-xs text-gray-500" data-testid="invoice-detail-label">
                              {r.detailLabel}
                            </span>
                          )}
                          {r.bill && (sharedBillCounts.get(r.bill.id) ?? 0) > 1 && (
                            <span
                              data-testid="invoice-shared-period"
                              className="mt-0.5 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800"
                              title={
                                `${sharedBillCounts.get(r.bill.id)} invoices resolved to this one bill period ` +
                                `(${fmtDate(r.bill.bill_from)} – ${fmtDate(r.bill.bill_to)}). The period, total and ` +
                                `verdict shown are the bill's, not this individual invoice's, and archiving ` +
                                `retires the period for all of them.`
                              }
                            >
                              1 of {sharedBillCounts.get(r.bill.id)} on this period
                            </span>
                          )}
                          {r.bill?.archived_at && (
                            <span
                              data-testid="invoice-archived-chip"
                              className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600"
                              title={[
                                archiveReasonLabel(r.bill.archive_reason),
                                r.bill.archive_note,
                                `Archived ${fmtDate(r.bill.archived_at)}`,
                              ].filter(Boolean).join(' · ')}
                            >
                              Archived — {archiveReasonLabel(r.bill.archive_reason)}
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 px-4 text-gray-700 whitespace-nowrap">
                          {r.needsPeriod ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                              <AlertTriangle className="h-3 w-3" /> Needs period
                            </span>
                          ) : fmtPeriod(r.bill)}
                        </td>
                        <td className="py-2.5 px-4">
                          {parse && (
                            <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', parse.cls)}>
                              {/* A spinner only while the parse is genuinely
                                  live. The old label span never moved, so a
                                  finished parse and a dead one looked the same
                                  as one in progress. */}
                              {r.parseStatus === 'parsing' && <Loader2 className="h-3 w-3 animate-spin" />}
                              {(r.parseStatus === 'failed' || r.parseStatus === 'stalled')
                                && <AlertTriangle className="h-3 w-3" />}
                              {parse.label}
                              {/* Ticks along with the 4s poll, so the chip is
                                  visibly moving rather than a frozen word. */}
                              {r.parseStatus === 'parsing' && r.parseElapsedMs != null && (
                                <span className="tabular-nums opacity-70">
                                  {formatElapsed(r.parseElapsedMs)}
                                </span>
                              )}
                            </span>
                          )}
                          {/* Job 76: "parse status if failed - why?". The reason
                              has been written to parse_error since job 68 and
                              displayed nowhere. */}
                          {r.parseStatus === 'failed' && (
                            <p
                              className="mt-1 max-w-[260px] text-[11px] leading-snug text-red-600"
                              title={r.parseErrorText ?? undefined}
                            >
                              {r.parseErrorText ?? 'No reason recorded — use Retry parse.'}
                            </p>
                          )}
                          {r.parseStatus === 'stalled' && (
                            <p className="mt-1 max-w-[260px] text-[11px] leading-snug text-amber-700">
                              Started but never finished — the parse was interrupted.
                              Use Retry parse.
                            </p>
                          )}
                        </td>
                        <td className="py-2.5 px-4 text-right text-gray-900 tabular-nums whitespace-nowrap">
                          {r.bill?.actual_total != null
                            ? new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(r.bill.actual_total)
                            : '—'}
                        </td>
                        <td className="py-2.5 px-4">
                          <span className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-semibold', VERDICT_STYLE[verdict])}>{verdict}</span>
                        </td>
                        <td className="py-2.5 px-4 text-right whitespace-nowrap">
                          <div className="inline-flex gap-1.5">
                            {/* Re-parse stays available AFTER a successful parse.
                                The parser is an LLM call and is not deterministic —
                                the same PDF has produced materially different results
                                on two runs (one inventing a 52% GST), so "already
                                parsed" is not the same as "parsed correctly". Hiding
                                the button once parsed left the Bill Run screen as the
                                only way to try again. */}
                            {r.bill && scope === 'active' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1"
                                // Also disabled while a parse is genuinely in
                                // flight: the button used to stay live under a
                                // "Parsing…" chip, so a second and third parse
                                // could be stacked on the same attachment.
                                // 'stalled' deliberately stays clickable — that
                                // is the state whose whole point is Retry.
                                disabled={parsingKey === r.key || r.parseStatus === 'parsing'}
                                title={r.parseStatus === 'parsing'
                                  ? 'A parse is already running for this invoice'
                                  : r.parseStatus === 'stalled'
                                    ? 'The previous parse was interrupted — start it again'
                                    : r.parseStatus === 'parsed'
                                      ? 'Parse this invoice again — all sections, or just the ones that came out wrong'
                                      : 'Extract line items from the invoice PDF'}
                                onClick={() => {
                                  // Both paths open the dialog now: the retailer
                                  // whose prompt drives the extraction is chosen
                                  // here, and a first parse needs that just as
                                  // much as a re-parse. The section chooser is
                                  // hidden when there is no stored result to
                                  // merge into.
                                  setReparseSections([]);
                                  setReparseNotes('');
                                  setReparseRetailerId(r.bill?.retailer_id ?? undefined);
                                  setParseError(null);
                                  setSentReportAt(null);
                                  setReparseRow(r);
                                  // Non-blocking: the dialog opens immediately
                                  // and the warning appears if there is one.
                                  if (r.bill) {
                                    void bvReportService.history(r.bill.id).then(res => {
                                      const sent = (res.data ?? []).find(h => h.status === 'sent');
                                      if (sent) setSentReportAt(sent.sent_at ?? sent.created_at);
                                    });
                                  }
                                }}
                              >
                                {parsingKey === r.key
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : <Sparkles className="h-3 w-3" />}
                                {r.parseStatus === 'failed' || r.parseStatus === 'stalled' ? 'Retry parse'
                                  : r.parseStatus === 'parsed' ? 'Re-parse'
                                  : r.parseStatus === 'parsing' ? 'Parsing…'
                                  : 'Parse'}
                              </Button>
                            )}
                            {scope === 'active' && r.parseStatus === 'parsed' && r.bill?.actual_parsed_result && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1"
                                title="Review and correct the parsed line items"
                                onClick={() => {
                                  const shared = (sharedBillCounts.get(r.bill!.id) ?? 0) > 1;
                                  if (shared) setEditWarnBill(r.bill!);
                                  else setEditingBill(r.bill!);
                                }}
                              >
                                <Pencil className="h-3 w-3" /> Edit parsed
                              </Button>
                            )}
                            {/* Only offered once something has been corrected —
                                there is nothing to compare against otherwise. */}
                            {r.bill && isInvoiceEdited(r.bill) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1 border-amber-300 text-amber-800 hover:bg-amber-50"
                                title="See exactly which values were changed, and what the parser originally read"
                                onClick={() => setComparingBill(r.bill!)}
                              >
                                <Pencil className="h-3 w-3" /> Edited — compare
                              </Button>
                            )}
                            {r.email && (
                              <Button size="sm" variant="outline" onClick={() => setPreviewEmail(r.email as InboxEmail)}>
                                View
                              </Button>
                            )}
                            {r.bill && (
                              <Button size="sm" variant="outline" className="gap-1" onClick={() => onOpenBillRun(r.bill!.id)}>
                                Open Bill Run <ExternalLink className="h-3 w-3" />
                              </Button>
                            )}
                            {/* Job 48 */}
                            {r.bill && scope === 'active' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1"
                                title="Correct the bill period without re-uploading the invoice"
                                onClick={() => openPeriodEditor(r)}
                              >
                                <CalendarDays className="h-3 w-3" /> Edit period
                              </Button>
                            )}
                            {/* Job 81 */}
                            {r.bill && scope === 'active' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1"
                                data-testid="invoice-archive-btn"
                                title="Archive this invoice with a reason — it stops counting, the PDF is kept"
                                onClick={() => { setArchiveError(null); setArchiving([r]); }}
                              >
                                <Archive className="h-3 w-3" /> Archive
                              </Button>
                            )}
                            {/* Only offered where it means something: an email
                                invoice sharing a period with another one. */}
                            {r.email && r.bill && scope === 'active'
                              && (sharedBillCounts.get(r.bill.id) ?? 0) > 1 && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                data-testid="invoice-remove-from-period"
                                title="Two invoices resolved to this bill period — remove the one that does not belong"
                                onClick={() => { setRemoveError(null); setRemovingRow(r); }}
                              >
                                <Trash2 className="h-3 w-3" /> Remove
                              </Button>
                            )}
                            {r.bill && scope === 'archived' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1"
                                data-testid="invoice-restore-btn"
                                title="Restore this invoice — it counts again"
                                onClick={() => void handleRestore([r])}
                              >
                                <ArchiveRestore className="h-3 w-3" /> Restore
                              </Button>
                            )}
                            {/* The Archived sub-tab is read-only: View, Open
                                Bill Run, Restore. Without the scope gate, one
                                click from there stripped an archived
                                invoice's PDF and parsed values. */}
                            {r.source === 'manual' && r.bill && scope === 'active' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                title="Delete the uploaded PDF — the bill period stays"
                                onClick={() => { setDeleteError(null); setDeleting(r); }}
                              >
                                <Trash2 className="h-3 w-3" /> Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reference invoices (job 55) — historic invoices with no bill period.
          Deliberately separate from the bill-linked list above; never parsed. */}
      <div className="mt-8 mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Reference invoices</h3>
          <p className="text-xs text-gray-500">
            Historic invoices kept for reference only — no bill period, never parsed
            and never part of any comparison or total.
          </p>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setRefError(null); setRefUploadOpen(true); }}>
          <Upload className="h-3.5 w-3.5" /> Upload reference
        </Button>
      </div>

      {refInvoices.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-8 gap-2 text-gray-400">
            <FileText className="h-7 w-7" />
            <p className="text-sm">No reference invoices for this site</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-gray-500">File</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-gray-500">Note</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-gray-500">Uploaded</th>
                  <th className="py-2.5 px-4 text-right text-xs font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {refInvoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-gray-50">
                    <td className="py-2.5 px-4 text-gray-700 max-w-[240px] truncate" title={inv.filename}>
                      <span className="inline-flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5 text-gray-400" /> {inv.filename}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-gray-600 max-w-[280px] truncate" title={inv.note ?? ''}>{inv.note || '—'}</td>
                    <td className="py-2.5 px-4 text-gray-700 whitespace-nowrap">{fmtDate(inv.created_at)}</td>
                    <td className="py-2.5 px-4 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => void handleRefView(inv)}>View</Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          onClick={() => setRefDeleting(inv)}
                        >
                          <Trash2 className="h-3 w-3" /> Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Upload a reference invoice — no bill period */}
      <Dialog open={refUploadOpen} onOpenChange={(v) => !v && !refUploading && setRefUploadOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload reference invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-gray-500">
              For historic invoices kept for reference only. No bill period is created —
              the file is never parsed and never contributes to any comparison or total.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Note (optional)</Label>
              <Input value={refNote} onChange={e => setRefNote(e.target.value)} placeholder="e.g. FY23 invoice for reference" />
            </div>
            <input
              ref={refFileRef}
              type="file"
              accept=".pdf,application/pdf"
              className="hidden"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) void handleRefUpload(f);
                e.target.value = '';
              }}
            />
            <div
              className={cn(
                'w-full flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-8 px-4 text-center transition-all',
                refUploading ? 'cursor-wait' : 'cursor-pointer',
                refDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/40',
              )}
              onDragOver={e => { e.preventDefault(); if (!refUploading) setRefDragOver(true); }}
              onDragLeave={() => setRefDragOver(false)}
              onDrop={handleRefDrop}
              onClick={() => { if (!refUploading) refFileRef.current?.click(); }}
            >
              {refUploading ? <Loader2 className="h-6 w-6 text-blue-600 animate-spin" /> : <Upload className="h-6 w-6 text-blue-600" />}
              <span className="text-sm font-medium text-gray-700">
                {refUploading ? 'Uploading…' : 'Drag & drop the invoice PDF here'}
              </span>
              {!refUploading && <span className="text-xs text-gray-500">or click to choose a file</span>}
            </div>
            <p className="text-xs text-gray-400">PDF only, max 20 MB.</p>
            {refError && <p className="text-sm text-red-600">{refError}</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete a reference invoice */}
      <Dialog open={!!refDeleting} onOpenChange={(v) => !v && !refDeleteBusy && setRefDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this reference invoice?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2.5 rounded-md bg-amber-50 border border-amber-200 p-3">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-medium break-all">{refDeleting?.filename}</p>
                <p className="mt-1 text-xs">The PDF is removed and can't be recovered.</p>
              </div>
            </div>
            {refError && <p className="text-sm text-red-600">{refError}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" disabled={refDeleteBusy} onClick={() => setRefDeleting(null)}>Cancel</Button>
              <Button
                className="gap-1.5 bg-red-600 hover:bg-red-700"
                disabled={refDeleteBusy}
                onClick={() => void handleRefDelete()}
              >
                {refDeleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {refDeleteBusy ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <EmailPreviewDrawer
        open={!!previewEmail}
        onClose={() => setPreviewEmail(null)}
        email={previewEmail}
        onAssigned={load}
      />

      {/* Job 81 — archive with a reason */}
      <ArchiveInvoiceDialog
        open={!!archiving}
        count={archiving?.length ?? 0}
        busy={archiveBusy}
        sharedPeriodNote={sharedPeriodNote}
        error={archiveError}
        onCancel={() => setArchiving(null)}
        onConfirm={(reason, note) => void handleArchive(reason, note)}
      />

      {/* Post-parse review: PDF left, editable parsed values right */}
      <InvoiceParseReview
        open={!!editingBill}
        bill={editingBill}
        onSave={handleSaveEdited}
        onRevert={async () => {
          if (!editingBill) return;
          const res = await billService.revertActualResult(editingBill.id);
          if (!res.success) throw new Error(res.error ?? 'Revert failed');
          setEditingBill(null);
          await load();
        }}
        onClose={() => setEditingBill(null)}
      />

      <InvoiceCompareDialog
        open={!!comparingBill}
        bill={comparingBill}
        onClose={() => setComparingBill(null)}
        onRevert={async () => {
          if (!comparingBill) return;
          const res = await billService.applyEditedInvoice(comparingBill.id, { revert: true });
          if (!res.success || !res.data) throw new Error(res.error ?? 'Revert failed');
          setEditNotice(
            res.data.usageUpdated.length === 0
              ? 'Corrections discarded — back to the parsed values.'
              : `Corrections discarded. Usage restored (${res.data.usageUpdated.join(', ')})`
                + `${res.data.repriced ? ' and the expected invoice re-priced.' : '.'}`,
          );
          await load();
        }}
      />

      {/* Correct the bill period in place (job 48) */}
      <Dialog open={!!editingPeriod} onOpenChange={(v) => !v && !periodBusy && setEditingPeriod(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit bill period</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Changes the period on this bill. The invoice, its parsed values and the
              usage figures are left as they are.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-gray-500">From</Label>
                <Input type="date" value={periodFrom} onChange={e => setPeriodFrom(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs text-gray-500">To</Label>
                <Input type="date" value={periodTo} onChange={e => setPeriodTo(e.target.value)} />
              </div>
            </div>

            {/* Job 48 / job 52: billing days feed the daily charges, so the
                calculated expected invoice goes stale — and a hand-corrected one
                must not look like it was silently thrown away. */}
            {editingPeriod?.bill?.shadow_result && (
              <p className={cn(
                'rounded-md border px-3 py-2 text-xs',
                isShadowOverridden(editingPeriod.bill.shadow_result)
                  ? 'border-amber-200 bg-amber-50 text-amber-900'
                  : 'border-blue-200 bg-blue-50 text-blue-900',
              )}>
                {isShadowOverridden(editingPeriod.bill.shadow_result)
                  ? 'This bill\'s expected invoice was edited manually. Your edit is kept — '
                    + 'but it was based on the old billing days, so review it on the Bill Runs tab.'
                  : 'The expected invoice is calculated from the number of billing days, '
                    + 'so recalculate it on the Bill Runs tab after changing the period.'}
              </p>
            )}

            {validateBillPeriod(periodFrom, periodTo) && (
              <p className="text-xs text-amber-700">{validateBillPeriod(periodFrom, periodTo)}</p>
            )}
            {periodError && <p className="text-sm text-red-600">{periodError}</p>}

            <div className="flex justify-end gap-2">
              <Button variant="outline" disabled={periodBusy} onClick={() => setEditingPeriod(null)}>
                Cancel
              </Button>
              <Button
                className="gap-1.5"
                disabled={periodBusy || !!validateBillPeriod(periodFrom, periodTo)}
                onClick={() => void handleSavePeriod()}
              >
                {periodBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}
                {periodBusy ? 'Saving…' : 'Save period'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete an uploaded invoice PDF — the bill period is deliberately kept */}
      <Dialog open={!!deleting} onOpenChange={(v) => !v && !deleteBusy && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this invoice?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2.5 rounded-md bg-amber-50 border border-amber-200 p-3">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-medium break-all">{deleting?.fromLabel}</p>
                <p className="mt-1 text-xs">
                  The PDF and the values parsed from it are removed and can't be recovered.
                  The bill period {fmtPeriod(deleting?.bill)} and its usage figures are kept,
                  so you can upload the correct invoice against it.
                </p>
              </div>
            </div>
            {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" disabled={deleteBusy} onClick={() => setDeleting(null)}>
                Cancel
              </Button>
              <Button
                className="gap-1.5 bg-red-600 hover:bg-red-700"
                disabled={deleteBusy}
                onClick={() => void handleDelete()}
              >
                {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {deleteBusy ? 'Deleting…' : 'Delete invoice'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Removing the surplus invoice from a shared bill period. */}
      <Dialog open={!!removingRow} onOpenChange={(v) => { if (!v && !removeBusy) setRemovingRow(null); }}>
        <DialogContent className="max-w-lg" data-testid="invoice-remove-dialog">
          <DialogHeader>
            <DialogTitle>Remove this invoice from the bill period?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-gray-600">
              {removingRow?.bill
                ? `${sharedBillCounts.get(removingRow.bill.id)} invoices resolved to ${fmtDate(removingRow.bill.bill_from)} – ${fmtDate(removingRow.bill.bill_to)}.`
                : null}{' '}
              A bill period holds one invoice&rsquo;s figures, so one of them has to go. This
              takes the email below off the period and moves it to the inbox&rsquo;s Ignored
              list; the other invoice keeps the period. Unlike Archive, the period stays active.
            </p>

            <div className="rounded-lg border bg-gray-50 px-3 py-2.5 space-y-1">
              <p className="text-xs text-gray-500">Removing</p>
              <p className="text-sm font-medium text-gray-900 break-all">{removingRow?.fromLabel}</p>
              {removingRow?.detailLabel && (
                <p className="text-xs text-gray-600 break-all">{removingRow.detailLabel}</p>
              )}
              <Button
                size="sm"
                variant="outline"
                className="mt-1 gap-1.5"
                onClick={() => removingRow?.email && setPreviewEmail(removingRow.email as InboxEmail)}
              >
                <FileText className="h-3.5 w-3.5" /> View this invoice first
              </Button>
            </div>

            {removingRow && ownsStoredFigures(removingRow) && (
              <div className="flex gap-2 items-start rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900">
                  <strong>The period&rsquo;s stored figures were read from this invoice.</strong>{' '}
                  Removing the email does not undo that, so the totals stay behind against the
                  invoice you keep. Re-parse the remaining invoice afterwards &mdash; the tab will
                  remind you.
                </p>
              </div>
            )}

            <p className="text-xs text-gray-500">The email stays in the inbox under Ignored and can be assigned again from there.</p>
            {removeError && <p className="text-xs text-red-600">{removeError}</p>}

            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" disabled={removeBusy} onClick={() => setRemovingRow(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="gap-1.5 bg-red-600 hover:bg-red-700"
                disabled={removeBusy}
                data-testid="invoice-remove-confirm"
                onClick={() => void handleRemoveFromPeriod()}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {removeBusy ? 'Removing…' : 'Remove invoice'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* "Edit parsed" on a shared period — corrections land on the bill, so
          they rewrite the stored figures for every invoice on it. */}
      <Dialog open={!!editWarnBill} onOpenChange={(v) => !v && setEditWarnBill(null)}>
        <DialogContent className="max-w-md" data-testid="edit-shared-period-dialog">
          <DialogHeader>
            <DialogTitle>This period holds more than one invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2 items-start rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-900">
                <strong>
                  {editWarnBill ? sharedBillCounts.get(editWarnBill.id) : 0} invoices resolved to
                  this bill period.
                </strong>{' '}
                {storedInvoiceName(editWarnBill ?? undefined)
                  ? <>The stored figures were read from <strong>{storedInvoiceName(editWarnBill ?? undefined)}</strong>.{' '}</>
                  : null}
                A bill keeps one set of figures, so your corrections apply to the period as a
                whole, not to a single invoice.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditWarnBill(null)}>Cancel</Button>
              <Button
                size="sm"
                data-testid="edit-shared-period-continue"
                onClick={() => { setEditingBill(editWarnBill); setEditWarnBill(null); }}
              >
                Edit anyway
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Re-parse — whole document, or only the sections that came out wrong */}
      <Dialog
        open={!!reparseRow}
        onOpenChange={(v) => { if (!v && !parsingKey) setReparseRow(null); }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{reparseRow?.parseStatus === 'parsed' ? 'Re-parse invoice' : 'Parse invoice'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-gray-500">
              Extraction is an AI read of the PDF and is not repeatable — the same invoice can
              parse differently twice. Narrow it to the sections that came out wrong and the rest
              of the stored result is left exactly as it is.
            </p>

            {sentReportAt && (
              <div className="flex gap-2 items-start rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900">
                  <strong>This bill&rsquo;s report was emailed to the client on{' '}
                  {new Date(sentReportAt).toLocaleDateString('en-AU', {
                    day: '2-digit', month: 'short', year: 'numeric',
                  })}.</strong>{' '}
                  Re-parsing can change the figures, and they will no longer match the PDF the
                  client holds. Send an updated report if you continue.
                </p>
              </div>
            )}

            {/* A shared period is the case where re-parsing destroys figures the
                user cannot see: the result is stored on the bill, so it belongs
                to whichever invoice parsed last, for all of them. */}
            {reparseRow?.bill && (sharedBillCounts.get(reparseRow.bill.id) ?? 0) > 1 && (
              <div
                data-testid="reparse-shared-period-warning"
                className="flex gap-2 items-start rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5"
              >
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900">
                  <strong>
                    {sharedBillCounts.get(reparseRow.bill.id)} invoices share this bill period.
                  </strong>{' '}
                  {storedInvoiceName(reparseRow.bill)
                    ? <>The figures stored against it were read from <strong>{storedInvoiceName(reparseRow.bill)}</strong>.{' '}</>
                    : null}
                  A bill keeps one set of parsed figures, so re-parsing replaces them for every
                  invoice on this period &mdash; including the ones you are not looking at, and
                  their verdicts.
                </p>
              </div>
            )}

            <RetailerPromptPicker
              value={reparseRetailerId ?? reparseRow?.bill?.retailer_id ?? null}
              onChange={setReparseRetailerId}
              siteRetailerName={reparseRow?.bill?.site?.current_retailer ?? null}
              disabled={!!parsingKey}
            />

            {reparseRow?.parseStatus === 'parsed' && (
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Sections to replace</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {(['retail', 'distribution', 'metering', 'environmental', 'other', 'gst'] as const).map(s => {
                  const on = reparseSections.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      disabled={!!parsingKey}
                      onClick={() => setReparseSections(prev =>
                        prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                      className={cn(
                        'rounded-md border px-2 py-1.5 text-xs font-medium capitalize transition-colors',
                        on
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300',
                      )}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400">
                {reparseSections.length === 0
                  ? 'None selected — the whole invoice is re-parsed and the current result, including any "Edit parsed" corrections, is replaced.'
                  : `Only ${reparseSections.join(', ')} will be replaced. Every other section is kept.`}
              </p>
            </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Extra instructions (optional)</Label>
              <Input
                value={reparseNotes}
                disabled={!!parsingKey}
                placeholder="e.g. the network charges are on page 3"
                onChange={e => setReparseNotes(e.target.value)}
              />
            </div>

            {parseError && <p className="text-sm text-red-600">{parseError}</p>}

            <div className="flex justify-end gap-2">
              <Button variant="outline" disabled={!!parsingKey} onClick={() => setReparseRow(null)}>
                Cancel
              </Button>
              <Button
                className="gap-1.5"
                disabled={!!parsingKey}
                onClick={async () => {
                  const row = reparseRow;
                  if (!row) return;
                  await handleParse(row, {
                    sections: reparseSections.length ? reparseSections : undefined,
                    extraInstructions: reparseNotes,
                    retailerId: reparseRetailerId,
                  });
                  setReparseRow(null);
                  setReparseRetailerId(undefined);
                }}
              >
                {parsingKey
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Sparkles className="h-4 w-4" />}
                {parsingKey ? 'Parsing…'
                  : reparseSections.length ? `Re-parse ${reparseSections.length} section${reparseSections.length > 1 ? 's' : ''}`
                  : reparseRow?.parseStatus === 'parsed' ? 'Re-parse everything'
                  : 'Parse invoice'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual invoice upload — for invoices that never arrived by email */}
      <Dialog open={uploadOpen} onOpenChange={(v) => !v && !uploading && setUploadOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Invoice PDF</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Bill period</Label>
              <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-0.5">
                {([
                  ['auto', 'Read from invoice'],
                  ['existing', 'Existing period'],
                  ['new', 'Enter dates'],
                ] as const).map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    disabled={uploading}
                    onClick={() => { setPeriodMode(mode); setUploadError(null); }}
                    className={cn(
                      'flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                      periodMode === mode
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {periodMode === 'auto' && (
                <p className="text-xs text-gray-500">
                  The billing period is read from the PDF. It already overrides anything entered
                  by hand, so there is nothing to fill in.
                </p>
              )}
              {periodMode === 'existing' && (
                <Select value={uploadBillId} onValueChange={setUploadBillId}>
                  <SelectTrigger>
                    <SelectValue placeholder={bills.length ? 'Select a bill period' : 'No bill periods yet — use "Read from invoice"'} />
                  </SelectTrigger>
                  <SelectContent>
                    {bills.map(b => (
                      <SelectItem key={b.id} value={b.id}>{fmtPeriod(b)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {periodMode === 'new' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-gray-500">From</Label>
                    <Input type="date" value={newFrom} onChange={e => setNewFrom(e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">To</Label>
                    <Input type="date" value={newTo} onChange={e => setNewTo(e.target.value)} />
                  </div>
                </div>
              )}
            </div>

            <input
              ref={fileRef}
              type="file"
              accept=".pdf,application/pdf"
              className="hidden"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) void handleUpload(f);
                e.target.value = '';
              }}
            />
            <div
              className={cn(
                'w-full flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-8 px-4 text-center transition-all',
                uploading ? 'cursor-wait' : 'cursor-pointer',
                dragOver
                  ? 'border-blue-500 bg-blue-50'
                  : periodReady
                    ? 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/40'
                    : 'border-gray-200 bg-gray-50',
              )}
              onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => { if (!uploading && periodReady) fileRef.current?.click(); }}
            >
              {uploading
                ? <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                : <Upload className="h-6 w-6 text-blue-600" />}
              <span className="text-sm font-medium text-gray-700">
                {uploading ? 'Uploading & parsing…' : 'Drag & drop the invoice PDF here'}
              </span>
              {!uploading && (
                <span className="text-xs text-gray-500">
                  {periodReady ? 'or click to choose a file' : 'select a bill period first'}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400">
              PDF only, max 20 MB. Parses automatically after upload — review with "Edit parsed".
            </p>
            {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
